import { createContext, forwardRef, useContext } from "react"
import type { ReactNode } from "react"
import { ExpandMore } from "@mui/icons-material"
import { Box, Button, buttonClasses, Collapse } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { ThreadListSurface } from "connected-components/ai-teammate/analytics/events"

// Collapsible-section header: a real button (aria-expanded) styled to read like the static ListSubheader.
const SECTION_TOGGLE_SX = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5,
  width: "100%", px: 2, py: 0, border: 0, background: "none", cursor: "pointer",
  font: "inherit", lineHeight: 2, fontWeight: 600, textAlign: "left",
  color: "var(--app-text-secondary)",
  "&:hover": { bgcolor: "action.hover" },
} satisfies SxProps<Theme>
const CHEVRON_SX = { fontSize: "1.25rem", transition: "transform 150ms ease" } satisfies SxProps<Theme>
const SHOW_MORE_ROW_SX = { display: "flex", justifyContent: "center", py: 0.5 } satisfies SxProps<Theme>
const SHOW_MORE_SX = {
  minWidth: 0, px: 1.5, py: 0.25, borderRadius: "6px",
  typography: "body2", textTransform: "none", fontWeight: 600,
  [`&:not(.${buttonClasses.contained})`]: { color: "var(--app-primary-ink)" },
  "&:hover": { backgroundColor: "var(--app-subtle-hover-surface)" },
  [`&.${buttonClasses.focusVisible}`]: { boxShadow: "0 0 0 2px var(--app-primary-ink)" },
  "& .MuiButton-endIcon": { ml: 0.5 },
  "& .MuiButton-endIcon > svg": {
    fontSize: "16px",
    transition: "transform 150ms ease",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
} satisfies SxProps<Theme>
const SHOW_MORE_CHEVRON_SX = { transform: "rotate(0deg)" } satisfies SxProps<Theme>
const SHOW_MORE_CHEVRON_EXPANDED_SX = { transform: "rotate(180deg)" } satisfies SxProps<Theme>

const SidePanelSurfaceContext = createContext<ThreadListSurface | null>(null)

export const SidePanelSurfaceProvider = SidePanelSurfaceContext.Provider

export function useSidePanelSurface(): ThreadListSurface | null {
  return useContext(SidePanelSurfaceContext)
}

export interface CollapsibleSectionProps {
  headingId: string
  label: string
  count?: number | string
  isCollapsed: boolean
  onToggleCollapse: () => void
  isForcedOpen?: boolean
  testId?: string
  children: ReactNode
}

export function CollapsibleSection({ headingId, label, count, isCollapsed, onToggleCollapse, isForcedOpen = false, testId, children }: CollapsibleSectionProps) {
  return (
    <Box component="section" aria-labelledby={headingId} data-testid={testId}>
      <Box
        component="button"
        type="button"
        id={headingId}
        onClick={onToggleCollapse}
        aria-expanded={!isCollapsed}
        data-testid={`chatbox-section-toggle-${label.toLowerCase()}`}
        sx={SECTION_TOGGLE_SX}
      >
        <span>{count == null ? label : `${label} (${count})`}</span>
        <ExpandMore sx={{ ...CHEVRON_SX, transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }} />
      </Box>
      <Collapse in={!isCollapsed || isForcedOpen} unmountOnExit>
        {children}
      </Collapse>
    </Box>
  )
}

interface SectionMoreButtonProps {
  onClick: () => void
  disabled?: boolean
  testId?: string
  disclosure?: { isExpanded: boolean, controlsId: string }
  children: ReactNode
}

export const SectionMoreButton = forwardRef<HTMLButtonElement, SectionMoreButtonProps>(
  function SectionMoreButton({ onClick, disabled, testId, disclosure, children }, ref) {
    const isExpanded = disclosure?.isExpanded ?? false
    return (
      <Box sx={SHOW_MORE_ROW_SX}>
        <Button
          ref={ref}
          variant="text"
          size="small"
          sx={SHOW_MORE_SX}
          disabled={disabled}
          onClick={onClick}
          data-testid={testId}
          aria-expanded={disclosure?.isExpanded}
          aria-controls={disclosure?.controlsId}
          endIcon={<ExpandMore sx={isExpanded ? SHOW_MORE_CHEVRON_EXPANDED_SX : SHOW_MORE_CHEVRON_SX} />}
        >
          {children}
        </Button>
      </Box>
    )
  },
)
